import _ from 'lodash';
import Constants from './Constants';
import { DecimalPrecisionError } from '../../V5/Errors';
import math, { subtract, gcd, lcm, exceedsMaximumPrecision } from '../MathHelpers';

const EXCEEDED_PRECISION_MESSAGE = `maximum number of decimal places supported is ${Constants.MAXIMUM_DECIMAL_PRECISION}`;

/**
 * Returns if the given value is valid with the given increment.
 * @param  {Number}  value     the value to check
 * @param  {Number}  increment the increment to check against
 * @return {Boolean}           true if and only if value is acceptable for the given increment.
 */
function isValueMultipleOfIncrement(value, increment) {
  const x = Math.fround(value / increment);
  return _.isInteger(x);
}

/**
 * @summary Returns if the given sequences have overlapping values
 * @param {Number} minimumA Minimum value for first sequence
 * @param {Number} minimumB Minimum value for second sequence
 * @param {Number} incrementA Increment value for first sequence
 * @param {Number} incrementB Increment value for second sequence
 * @return {Boolean} true if the given sequences overlap, false otherwise
 * @desc Given the sequences (minimumA + incrementA*m) and (minimumB + incrementB*n) we can determine overlap by
 * setting the equations equal to each other resulting in (incrementA*m - incrementB*n = minimumB - minimumA).
 * This is a Linear Diophantine Equation (https://en.wikipedia.org/wiki/Diophantine_equation#Linear_Diophantine_equations)
 * so we can determine whether or not a solution exists by checking if (gcd(incrementA, incrementB) | minimumB - minB)
 */
function doSequencesOverlap(minimumA, minimumB, incrementA, incrementB) {
  const incrementGCD = gcd(incrementA, incrementB);

  const minimumADecimal = math.bignumber(minimumA);
  const minimumBDecimal = math.bignumber(minimumB);
  const incrementGCDDecimal = math.bignumber(incrementGCD);

  return math
    .chain(minimumADecimal)
    .subtract(minimumBDecimal)
    .abs()
    .mod(incrementGCDDecimal)
    .equal(0)
    .done();
}

/**
 * If maximum is null make it Infinity to make internal calculations easier. If the range
 * is discrete normalize the maximum by finding the largest valid value on the given increment.
 * @param {Number} minimum minimum value for the range
 * @param {Number} maximum maximum value for the range
 * @param {Number} increment increment for the range
 * @return {Number} Normalized maximum
 */
function normalizeMaximum(minimum, maximum, increment) {
  let finalMaximum = maximum;
  if (_.isNil(maximum) || !_.isFinite(maximum)) {
    finalMaximum = Infinity;
  } else if (!_.isNil(increment)) {
    const maximumDecimal = math.bignumber(maximum);
    const minimumDecimal = math.bignumber(minimum);
    const incrementDecimal = math.bignumber(increment);

    const maximumOffsetDecimal = math.mod(subtract(maximumDecimal, minimumDecimal), incrementDecimal);

    finalMaximum = math.number(subtract(maximum, maximumOffsetDecimal));
  }

  return finalMaximum;
}

function validate(minimum, max, increment) {
  if (_.isNil(minimum)) {
    throw new Error('minimum is required');
  }
  if (minimum > max) {
    throw new Error('minimum must be less than or equal to maximum');
  }
  if (minimum && increment < 0) {
    throw new Error('increment must be positive');
  }
  if (exceedsMaximumPrecision(minimum)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }
  if (_.isFinite(max) && exceedsMaximumPrecision(max)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }
  if (increment && exceedsMaximumPrecision(increment)) {
    throw new DecimalPrecisionError(EXCEEDED_PRECISION_MESSAGE);
  }
}

class Range {
  static getSmallestPossibleIncrement() {
    return Number(Number(`1e-${Constants.MAXIMUM_DECIMAL_PRECISION}`).toFixed(Constants.MAXIMUM_DECIMAL_PRECISION));
  }

  /**
   * Creates a new Range model.
   * @param {Number} minimum - the minimum value for the range.
   * @param {Number} maximum - the maximum value for the range, optional. If omitted, the range is unbounded.
   * @param {Number} increment - the increment for the range, optional. If omitted, the range is continuous.
   */
  constructor(minimum, maximum, increment, unitOfMeasure) {
    this.minimum = minimum;
    this.maximum = normalizeMaximum(minimum, maximum, increment);

    // Increment only applies when the range contains more than 1 value.
    if (this.minimum === this.maximum) {
      this.increment = undefined;
    } else {
      this.increment = increment;
    }

    this.unitOfMeasure = unitOfMeasure;

    validate(this.minimum, this.maximum, this.increment);
  }

  /**
   * Whether this range contains the given value.
   * @param value {Range|Number|String} - The value which may be contained by this range.
   * @returns {boolean} True when the value is within the range, otherwise false.
   */
  contains(value) {
    let isContainedBy;
    if (_.isNil(value)) {
      isContainedBy = false;
    } else if (value instanceof Range) {
      if (this.isContinuous()) {
        isContainedBy = this.containsRange(value);
      } else if (this.isDiscrete() && value.isDiscrete()) {
        const intersectionIncrement = lcm(this.increment, value.increment);

        isContainedBy = intersectionIncrement === value.increment && this.containsRange(value) && this.overlaps(value);
      }
    } else {
      isContainedBy = this.containsValue(value);
    }

    return isContainedBy;
  }

  /**
   * Whether this range contains the given value.
   * @param value - a value which may be contained by this range.
   * @returns {boolean} True when the value is within the range, otherwise false.
   * @private
   */
  containsValue(value) {
    if (value === null || value < this.minimum || (_.isNil(this.maximum) === false && value > this.maximum)) {
      return false;
    }

    const diffMinimum = value - this.minimum;
    if (diffMinimum === 0) {
      return true;
    }

    if (!_.isNil(this.increment)) {
      return isValueMultipleOfIncrement(diffMinimum, this.increment);
    }

    // increment is not specified and it's within the min/maximum bounds
    return true;
  }

  /**
   * Whether the given range is contained by this range
   * @param otherRange - A range which may be contained by this range.
   * @returns {boolean} - True when this range contains the given range, otherwise false.
   * @private
   */
  containsRange(otherRange) {
    return this.minimum <= otherRange.minimum && this.maximum >= otherRange.maximum;
  }

  /**
   * Whether the given range has overlapping values with this range.
   * @param otherRange - A range to overlap check against this range.
   * @returns {boolean} - True when this range overlaps with the given range, otherwise false.
   * @private
   */
  overlaps(otherRange) {
    let boundariesOverlap = this.minimum <= otherRange.maximum && this.maximum >= otherRange.minimum;

    if (this.isDiscrete() && otherRange.isDiscrete()) {
      const hasSharedValues = doSequencesOverlap(this.minimum, otherRange.minimum, this.increment, otherRange.increment);

      boundariesOverlap = boundariesOverlap && hasSharedValues && this.findMinimalCommonValue(otherRange) !== null;
    }

    return boundariesOverlap;
  }

  /**
   * Calculate the minimum intersected value by iterating the ranges
   * @param otherRange - A range to intersect with this range.
   * @returns {number | null} - The minimum intersected value, null if none exists
   * @private
   */
  findMinimalCommonValue(otherRange) {
    const smallerMaximum = math.min(this.maximum, otherRange.maximum);
    let minimum1 = math.bignumber(this.minimum);
    let minimum2 = math.bignumber(otherRange.minimum);

    while (!math.equal(minimum1, minimum2) && (math.smaller(minimum1, smallerMaximum) || math.smaller(minimum2, smallerMaximum))) {
      if (math.smaller(minimum1, minimum2)) {
        minimum1 = minimum1.add(this.increment);
      } else {
        minimum2 = minimum2.add(otherRange.increment);
      }
    }

    return math.equal(minimum1, minimum2) ? math.number(minimum1) : null;
  }

  /**
   * Whether the given range has the same boundaries as this range.
   * @param otherRange - A range to check for equal boundaries.
   * @returns {boolean} - True when the given range's minimum/maximum boundaries match this range's.
   */
  hasEqualBoundaries(otherRange) {
    return this.minimum === otherRange.minimum && this.maximum === otherRange.maximum;
  }

  /**
   * Whether this range contains a single value, meaning the range can be implied to be that value.
   * @returns {boolean} - True when this range's minimum equals its maximum.
   */
  isImpliable() {
    return this.minimum === this.maximum;
  }

  isContinuous() {
    return _.isNil(this.increment);
  }

  isDiscrete() {
    return !_.isNil(this.increment);
  }
}

module.exports = Range;
